import { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { GraphChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

echarts.use([GraphChart, TooltipComponent, CanvasRenderer]);

const STATS = [
  { value: "51,095+", label: "ENTITIES" },
  { value: "506,000+", label: "RELATIONS" },
];

type KgNode = {
  id: string;
  group: "main1" | "main2" | "attr";
  radius: number;
};

type KgLink = {
  source: string;
  target: string;
  label: string;
  type?: "special";
};

function wrapLabel(text: string, maxCharsPerLine: number) {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 1) return text;

  const lines: string[] = [];
  let line: string[] = [];

  for (const word of words) {
    const next = line.length ? `${line.join(" ")} ${word}` : word;
    if (next.length > maxCharsPerLine && line.length) {
      lines.push(line.join(" "));
      line = [word];
      continue;
    }
    line.push(word);
  }

  if (line.length) lines.push(line.join(" "));
  return lines.join("\n");
}

export default function KnowledgeGraphSection() {
  const chartDomRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.EChartsType | null>(null);

  const data = useMemo(() => {
    const nodes: KgNode[] = [
      { id: "Nvidia", group: "main1", radius: 34 },
      { id: "AMD", group: "main2", radius: 34 },

      { id: "GPUs", group: "attr", radius: 24 },
      { id: "AI Accel.", group: "attr", radius: 24 },
      { id: "NASDAQ", group: "attr", radius: 24 },
      { id: "Intel", group: "attr", radius: 24 },
      { id: "Santa Clara", group: "attr", radius: 24 },
      { id: "TSMC", group: "attr", radius: 24 },

      { id: "CPUs", group: "attr", radius: 24 },
      { id: "Sony", group: "attr", radius: 24 },
      { id: "Xilinx", group: "attr", radius: 24 },
      { id: "Microsoft", group: "attr", radius: 24 },
      { id: "Austin", group: "attr", radius: 24 },
    ];

    const links: KgLink[] = [
      { source: "Nvidia", target: "AMD", label: "Co-rising", type: "special" },

      { source: "Nvidia", target: "GPUs", label: "produce" },
      { source: "Nvidia", target: "AI Accel.", label: "produce" },
      { source: "Nvidia", target: "NASDAQ", label: "company_belong_to_exchange" },
      { source: "Nvidia", target: "Intel", label: "same_industry" },
      { source: "Nvidia", target: "Santa Clara", label: "company_locate_in_city" },
      { source: "Nvidia", target: "TSMC", label: "supply" },

      { source: "AMD", target: "CPUs", label: "produce" },
      { source: "AMD", target: "GPUs", label: "produce" },
      { source: "AMD", target: "NASDAQ", label: "company_belong_to_exchange" },
      { source: "AMD", target: "Sony", label: "supply" },
      { source: "AMD", target: "Xilinx", label: "invest" },
      { source: "AMD", target: "Microsoft", label: "supply" },
      { source: "AMD", target: "Austin", label: "company_locate_in_city" },
    ];

    return { nodes, links };
  }, []);

  const option: EChartsOption = useMemo(() => {
    const nodeColor = (group: KgNode["group"]) => {
      if (group === "main1") return "#2563eb";
      if (group === "main2") return "#059669";
      return "#0f172a";
    };

    return {
      backgroundColor: "transparent",
      animation: true,
      tooltip: {
        trigger: "item",
        confine: true,
        backgroundColor: "rgba(255, 255, 255, 0.96)",
        borderColor: "#e2e8f0",
        borderWidth: 1,
        padding: 10,
        textStyle: { color: "#0f172a", fontSize: 12 },
        extraCssText: "box-shadow: 0 10px 15px -3px rgb(15 23 42 / 0.12);",
        formatter: (p: any) => {
          if (p?.dataType === "node") {
            return `<div style="font-size:12px;"><div style="font-weight:700; margin-bottom:6px;">${p?.name ?? ""
              }</div><div style="opacity:0.75;">Entity</div></div>`;
          }
          if (p?.dataType === "edge") {
            const label = p?.data?.name ?? "";
            return `<div style="font-size:12px;"><div style="font-weight:700; margin-bottom:6px;">${label
              }</div><div style="opacity:0.75;">Relationship</div></div>`;
          }
          return "";
        },
      },
      series: [
        {
          type: "graph",
          layout: "force",
          roam: true,
          draggable: true,
          zoom: 0.7,
          symbol: "circle",
          edgeSymbol: ["none", "arrow"],
          edgeSymbolSize: 10,
          force: {
            repulsion: 900,
            edgeLength: 150,
            gravity: 0.05,
          },
          label: {
            show: true,
            color: "#ffffff",
            fontSize: 12,
            fontWeight: 600,
            align: "center",
            verticalAlign: "middle",
            formatter: (p: any) => wrapLabel(String(p?.name ?? ""), 9),
          },
          lineStyle: {
            color: "#94a3b8",
            width: 1.5,
            opacity: 0.8,
          },
          edgeLabel: {
            show: true,
            color: "#64748b",
            fontSize: 9,
            backgroundColor: "transparent",
            borderWidth: 0,
            padding: 0,
            formatter: (p: any) => String(p?.data?.name ?? ""),
          },
          data: data.nodes.map((n) => ({
            name: n.id,
            symbolSize: n.radius * 2,
            itemStyle: {
              color: nodeColor(n.group),
              borderColor: "rgba(255, 255, 255, 0.85)",
              borderWidth: 1.5,
            },
          })),
          links: data.links.map((l) => {
            const isSpecial = l.type === "special";
            return {
              source: l.source,
              target: l.target,
              name: l.label,
              value: 0,
              lineStyle: {
                color: isSpecial ? "#ef4444" : "#94a3b8",
                width: isSpecial ? 2 : 1.5,
                opacity: isSpecial ? 0.95 : 0.75,
              },
              label: {
                color: isSpecial ? "#ef4444" : "#475569",
                fontWeight: isSpecial ? 700 : 500,
                fontSize: isSpecial ? 11 : 9,
              },
            };
          }),
        },
      ],
    };
  }, [data.links, data.nodes]);

  useEffect(() => {
    if (!chartDomRef.current) return;
    const dom = chartDomRef.current;

    const instance = echarts.init(dom, undefined, { renderer: "canvas" });
    chartRef.current = instance;
    instance.setOption(option, { notMerge: true });

    const ro = new ResizeObserver(() => instance.resize());
    ro.observe(dom);

    return () => {
      ro.disconnect();
      instance.dispose();
      chartRef.current = null;
    };
  }, [option]);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white via-slate-50 to-white text-slate-900">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-28 left-[-120px] h-[440px] w-[440px] rounded-full bg-gradient-to-br from-cyan-200/35 via-sky-200/25 to-indigo-200/25 blur-3xl" />
        <div className="absolute -bottom-36 right-[-160px] h-[520px] w-[520px] rounded-full bg-gradient-to-br from-emerald-200/30 via-lime-200/20 to-cyan-200/25 blur-3xl" />
      </div>

      <div className="relative mx-auto w-full max-w-[1280px] px-6 py-16 md:px-8 md:py-20">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="mt-6 text-balance text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">
            Turn market news into a
            <span className="text-blue-600"> connected view</span>
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-pretty text-lg leading-8 text-slate-600">
            {/* Extract entities and relationships from news, then visualize how companies, products, and exchanges connect. */}
             Convert fragmented market text into interpretable structured knowledge through extraction, fusion, and incremental update.
          </p>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-900/10 bg-white/60 p-6 shadow-sm">
                <h3 className="text-xl font-semibold tracking-tight text-slate-900">
                  More <span className="text-blue-600">Diverse</span> Downstream Applications
                  <br />
                  More <span className="text-blue-600">Knowledge Types</span> in Four Hierarchy
                </h3>

                <div className="mt-6 space-y-6 text-sm leading-relaxed text-slate-700">
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Compare with existing public KBs
                  </div>

                  <div>
                    <div className="text-slate-500">Support up to</div>
                    <div className="mt-1 text-2xl font-bold tracking-tight text-slate-900">5 KB-Enhanced Applications</div>
                    <div className="mt-1 text-slate-600">Existing KB only support at most 3 applications.</div>
                  </div>

                  <div>
                    <div className="text-2xl font-bold tracking-tight text-slate-900">
                      Knowledge Types Cover
                      <br />
                      4 Economics Hierarchy
                    </div>
                    <div className="mt-1 text-slate-600">Including Macro, Meso, Micro and Others</div>
                  </div>
                </div>
              </div>

              {/* <div className="mt-3 rounded-2xl border border-slate-900/10 bg-white/75 p-6 shadow-sm">
                <dl className="grid gap-4">
                  {STATS.map((s) => (
                    <div key={`${s.value}-${s.label}`} className="flex items-baseline justify-between gap-4">
                      <div className="text-2xl font-bold tracking-tight text-slate-900">{s.value}</div>
                      <div className="text-xs font-semibold tracking-wide text-slate-600">{s.label}</div>
                    </div>
                  ))}
                </dl>
              </div> */}
            </div>


          </div>

          <div className="lg:col-span-7">
            <div className="rounded-2xl border border-slate-900/10 bg-white/75 shadow-sm">
              <div className="flex items-center justify-between gap-4 border-b border-slate-900/10 px-6 py-4">
                <div className="text-sm font-semibold tracking-tight text-slate-900">Relationship Graph</div>
                {/* <div className="text-xs font-medium text-slate-500">Drag nodes • Scroll to zoom</div> */}
              </div>
              <div className="p-4">
                <div
                  ref={chartDomRef}
                  className="h-[360px] w-full overflow-hidden rounded-xl bg-gradient-to-b from-white to-slate-50 md:h-[420px]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
